import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Eye, Lock, Pencil, Play, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { MODES } from "@/modes";
import { RoomSettingsForm } from "@/components/game/SettingFields";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ActivityFields, blankData, TEMPLATES, toApi, toForm } from "@/components/teacher/ActivityFields";
import { EMPTY_FILTER, ListFilter, Pager, useFiltered, usePaged } from "@/components/teacher/ListFilter";
import { AccessBadge, ShareButton, ShareDialog } from "@/components/teacher/ShareDialog";
import { EMPTY_TAGS, LikeButton, TagChips, TagPicker, tagsOf } from "@/components/teacher/Social";
import { SettingTemplates } from "@/components/teacher/SettingTemplates";
import { PacksSection } from "@/components/teacher/PacksSection";
import { LockButton, LockedBadge } from "@/components/teacher/SiteAdmin";
import { InfoTip } from "@/components/game/SettingFields";
import { useCatalog } from "@/lib/catalog";
import { useAuth } from "@/lib/auth";
import { ContentBadges, trackView } from "@/components/teacher/ContentBadges";
import { ConsentCheck, ROOM_CONSENT } from "@/components/Legal";
import { Showcase } from "@/components/teacher/Showcase";

const previewActivity = async (a) => {
  trackView("activities", a.id);
  try {
    const { data } = await api.post("/preview", { activity_id: a.id, settings: { team_count: 0 } });
    window.open(`/onizleme/${data.code}`, "_blank");
  } catch (e) { toast.error(errMsg(e)); }
};

const actText = (a) => `${a.title} ${TEMPLATES[a.template]?.label || ""}`;

const TEMPLATE_MODE = { bingo: "bingo", classification: "sort", board_classification: "boardsort", matching: "sort", written: "written" };
const GENERIC = { team_count: 3, team_selection: "student", seats_per_team: 8, board_names: "initial", sound: true,
  scoring: "team", rank_display: "full", board_individual_podium: false };
const count = (a) => ({ bingo: a.data.items?.length, classification: a.data.items?.length, board_classification: a.data.items?.length, matching: a.data.pairs?.length, written: a.data.prompts?.length })[a.template];

const Editor = ({ open, onOpenChange, activity, onSaved }) => {
  useCatalog();
  const dev = Boolean(useAuth().teacher?.developer);
  const [template, setTemplate] = useState("bingo");
  const [title, setTitle] = useState("");
  const [data, setData] = useState(blankData("bingo"));
  const [busy, setBusy] = useState(false);
  const [tags, setTags] = useState(EMPTY_TAGS);
  useEffect(() => {
    if (!open) return;
    setTemplate(activity?.template || "bingo");
    setTitle(activity?.title || "");
    setData(activity ? toForm(activity) : blankData("bingo"));
    setTags(tagsOf(activity));
  }, [open, activity]);
  const save = async () => {
    setBusy(true);
    try {
      const body = { template, title, data: toApi(template, data) };
      const res = activity ? await api.put(`/activities/${activity.id}`, body) : await api.post("/activities", body);
      const id = activity?.id || res.data.id;
      if (tags.theme_id || tags.grade || activity?.theme_id || activity?.grade) await api.put(`/tags/activities/${id}`, tags);
      toast.success("Etkinlik kaydedildi");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="activity-editor">
        <DialogHeader><DialogTitle className="font-display text-3xl">{activity ? "Etkinliği düzenle" : "Yeni etkinlik"}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          {!activity && (
            <div className="grid sm:grid-cols-2 gap-2">
              {Object.entries(TEMPLATES).map(([k, t]) => (
                <button key={k} type="button" disabled={t.locked && !dev} onClick={() => { setTemplate(k); setData(blankData(k)); }} data-testid={`template-${k}`}
                  className={`text-left rounded-lg border px-4 py-3 ${t.locked && !dev ? "opacity-40 cursor-not-allowed" : ""} ${template === k ? "border-[#FACC15] bg-[#FACC15]/10" : "border-[rgba(71,85,105,0.45)]"}`}>
                  <p className="font-bold flex items-center gap-2">{t.locked && <Lock className="h-4 w-4" />}{t.label}<InfoTip text={t.desc} testId={`template-info-${k}`} /></p>
                </button>
              ))}
            </div>
          )}
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Etkinlik adı" className="h-12 text-lg" data-testid="activity-title-input" />
          <ActivityFields template={template} data={data} setData={setData} />
          <TagPicker value={tags} onChange={setTags} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="activity-cancel">Vazgeç</Button>
          <Button onClick={save} disabled={busy || !title.trim()} className="font-bold px-6" data-testid="activity-save">Kaydet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StartDialog = ({ activity, onOpenChange }) => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(GENERIC);
  const [consent, setConsent] = useState(false);
  const mode = activity && TEMPLATE_MODE[activity.template];
  const start = async () => {
    trackView("activities", activity.id);
    try {
      const { data } = await api.post(`/activities/${activity.id}/start`, { settings });
      navigate(`/ogretmen/oyun/${data.code}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={Boolean(activity)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="activity-start-dialog">
        <DialogHeader><DialogTitle className="font-display text-3xl">Oyun olarak başlat</DialogTitle></DialogHeader>
        {activity && <SettingTemplates mode={mode} settings={settings} onApply={(s) => setSettings((x) => ({ ...x, ...s }))} />}
        {activity && <RoomSettingsForm settings={settings} onChange={(p) => setSettings((s) => ({ ...s, ...p }))} ModeForm={MODES[mode]?.SettingsForm} />}
        <ConsentCheck checked={consent} onChange={setConsent} testId="activity-start-consent">{ROOM_CONSENT}</ConsentCheck>
        <DialogFooter><Button onClick={start} disabled={!consent} className="font-bold px-6" data-testid="activity-start-confirm"><Play className="h-4 w-4 mr-2" />Oyunu oluştur</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function ActivitiesPage() {
  useCatalog();
  const dev = Boolean(useAuth().teacher?.developer);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [starting, setStarting] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [filter, setFilter] = useState(EMPTY_FILTER);
  const [sharing, setSharing] = useState(null);
  const shown = useFiltered(items, filter, actText);
  const pg = usePaged(shown, 6);
  const load = useCallback(() => { api.get("/activities").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async () => {
    await api.delete(`/activities/${removing.id}`).catch((e) => toast.error(errMsg(e)));
    setRemoving(null);
    load();
  };
  const duplicate = async (a) => {
    try {
      await api.post(`/activities/${a.id}/duplicate`);
      toast.success("Etkinlik çoğaltıldı");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-8" data-testid="activities-page">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1"><p className="eyebrow">Etkinlik Oluşturucu</p><h1 className="font-display text-4xl sm:text-5xl font-black">Etkinlikler</h1></div>
        <Button onClick={() => setEditing({})} className="font-bold" data-testid="activity-new-button"><Plus className="h-4 w-4 mr-2" />Yeni etkinlik</Button>
      </div>
      {items.length === 0 && <p className="text-slate-400" data-testid="activities-empty">Henüz etkinlik yok. Tombala, sınıflandırma, eşleştirme veya yazılı yanıt şablonuyla başlayın.</p>}
      {items.length > 0 && <ListFilter items={items} value={filter} onChange={setFilter} placeholder="Etkinlik ya da öğretmen ara…" />}
      {items.length > 0 && shown.length === 0 && <p className="text-slate-400" data-testid="activities-no-match">Aramaya uygun etkinlik yok.</p>}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {pg.items.map((a) => {
          const off = (a.locked || TEMPLATES[a.template]?.locked) && !dev;
          return (
          <div key={a.id} className={`panel p-5 space-y-3 ${a.locked || TEMPLATES[a.template]?.locked ? "opacity-60" : ""}`} data-testid={`activity-card-${a.id}`}>
            <p className="eyebrow text-[#FACC15] flex items-center gap-2">{TEMPLATES[a.template].label} · {count(a)} öğe <InfoTip text={TEMPLATES[a.template].desc} testId={`activity-info-${a.id}`} /> <LockedBadge item={{ id: a.id, locked: a.locked || TEMPLATES[a.template]?.locked }} /></p>
            <p className="font-display text-xl font-bold">{a.title}</p>
            <p className="text-xs text-slate-500" data-testid={`activity-owner-${a.id}`}>Oluşturan: <span className="text-slate-300">{a.owner_name}</span> <AccessBadge item={a} /></p>
            <div className="flex items-center gap-2"><LikeButton kind="activities" item={a} /><TagChips item={a} /><ContentBadges kind="activities" item={a} onChange={load} /></div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" disabled={off} onClick={() => setStarting(a)} data-testid={`activity-start-${a.id}`}><Play className="h-4 w-4 mr-1" />Başlat</Button>
              <Button size="sm" variant="outline" disabled={off} onClick={() => previewActivity(a)} data-testid={`activity-preview-${a.id}`}><Eye className="h-4 w-4 mr-1" />Önizle</Button>
              {a.mine && <Button size="sm" variant="outline" onClick={() => setEditing(a)} data-testid={`activity-edit-${a.id}`}><Pencil className="h-4 w-4 mr-1" />Düzenle</Button>}
              <Button size="sm" variant="outline" disabled={off} onClick={() => duplicate(a)} data-testid={`activity-duplicate-${a.id}`}><Copy className="h-4 w-4 mr-1" />Çoğalt</Button>
              <ShareButton item={a} onClick={setSharing} />
              <LockButton kind="activities" item={a} onChange={load} />
              {a.mine && <Button size="sm" variant="ghost" onClick={() => setRemoving(a)} className="text-red-400" data-testid={`activity-delete-${a.id}`}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          </div>
          );
        })}
      </div>
      <Pager pg={pg} testId="activities-pager" />
      <Showcase kind="activities" items={items} onOpen={(a) => setStarting(a)} />
      <PacksSection kind="activities" items={items} onCopied={load} onEditContent={(p) => { const a = items.find((x) => x.id === p.item_id); if (a) setEditing(a); }} />
      <Editor open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)} activity={editing?.id ? editing : null} onSaved={load} />
      <StartDialog activity={starting} onOpenChange={(o) => !o && setStarting(null)} />
      <ShareDialog kind="activities" item={sharing} onOpenChange={(o) => !o && setSharing(null)} onSaved={load} />
      <ConfirmDialog open={Boolean(removing)} onOpenChange={(o) => !o && setRemoving(null)} title="Etkinlik silinsin mi?"
        description="Etkinlik ve sınıf rekoru kayıtları silinir; bu etkinlikle oynanmış oyunların analizleri korunur." confirmText="Sil" onConfirm={remove} />
    </div>
  );
}
